import _ from 'lodash'
import React, { CSSProperties } from 'react'
import classNames from 'classnames'
import { objectToString } from 'shared-libs/helpers/formulas/modules'

import { Entity } from 'shared-libs/models/entity'
import {
  EntityPreviewCardCellType,
  EntityPreviewCardCellProps as IEntityPreviewCardCellProps,
  EntityPreviewCardProps as IEntityPreviewCardSharedProps,
} from 'shared-libs/components/ui2/display/entity-preview-card/entity-preview-card-props'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { StatusBadge } from 'browser/components/atomic-elements/atoms/status-badge/status-badge'
import { EntityFormSheetMode } from '../entity-form-sheet'
import { EntityFormModal } from '../entity-form-modal'
import { Modal } from 'browser/components/atomic-elements/atoms/modal'
import OverlayManager from 'browser/components/atomic-elements/organisms/overlay-manager/overlay-manager'
import { LoadingSpinner } from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'
import { SheetContext } from 'browser/components/atomic-elements/atoms/sheet/sheet-manager'
import 'browser/components/atomic-elements/organisms/entity/form-entity-preview-card/_form-entity-preview-card.scss'
import apis from 'browser/app/models/apis'
import { evaluateExpressionWithScopes } from 'shared-libs/helpers/evaluation'
import { AsyncFormulaResolver } from 'shared-libs/helpers/async-formula-resolver'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import { Settings } from 'browser/app/models/settings'
import { Label, ILabelProps } from 'browser/components/atomic-elements/atoms/label/label'
import { getWebEntityDisplay } from 'browser/app/models/entity-display-registry'
import { EntityPreviewCardContext } from './form-entity-preview-card-context'

/**
 * Interaction matrix for the click/tap handler.
 *
 * | isDisabled | isReadOnly | entityReadOnlySchemaPath | entityEditSchemaPath | Viewer's firm owns entity? | Result                                            |
 * |------------|------------|--------------------------|----------------------|----------------------------|---------------------------------------------------|
 * | true       | any        | any                      | any                  | any                        | Muted card, no-op                                 |
 * | false      | true       | resolved                 | any                  | any                        | Interactive card, opens read-only sheet           |
 * | false      | true       | unresolved               | any                  | any                        | Muted card, no-op                                 |
 * | false      | false      | any                      | any                  | yes                        | Interactive card, opens edit sheet (tab fallback) |
 * | false      | false      | resolved                 | any                  | no                         | Interactive card, opens read-only sheet           |
 * | false      | false      | unresolved               | any                  | no                         | Muted card, no-op                                 |
 *
 * Ownership: `entity.owner.firm.entityId === apis.getSettings().firm.uniqueId`.
 * "Resolved" means `entity.resolveSubschemaByPath(path)` returns a schema.
 *
 * Deprecated props kept out of this component (present in legacy
 * `ui:entityPreviewCard`, absent here):
 *   onClick, isClickable, entityDetailSchema, entitySheetMode,
 *   label, labelProps, isListOption, useHorizontalLayout.
 * See `FormEntityPreviewCardWithLabel` below for the deprecated
 * label-wrapping helper kept for one-off backward compatibility.
 */

type ClickResolution = 'edit' | 'readonly' | 'none'

export interface IFormEntityPreviewCardProps extends IBaseProps, IEntityPreviewCardSharedProps {
  frames?: FramesManager
  openOverlay?: any
  style?: CSSProperties
  /**
   * Component registry passed to `EntityFormModal` when the card opens
   * an entity for edit / view. Defaults to the DW `browser/components`
   * map inside the modal. MW's `MobileFormEntityPreviewCard` wrapper
   * passes `MobileComponents` so the modal renders MW-native fields
   * when opened from a mobile-web surface.
   */
  componentsMap?: Record<string, any>
  /**
   * Class applied to the `EntityFormModal` root when this card's click
   * opens one — used by the MW wrapper to select the MW variant
   * (`c-entityFormModal--mobileWeb`).
   */
  entityFormModalClassName?: string
}

interface IFormEntityPreviewCardState {
  entity: Entity
  isLoading: boolean
}

/**
 * FormEntityPreviewCard - Card component showing a formatted summary of an
 * entity. Clicking the card resolves via the interaction matrix above to
 * open the entity's edit sheet, read-only sheet, or nothing at all — no
 * custom click handlers are supported.
 */
export class FormEntityPreviewCard extends React.PureComponent<
  IFormEntityPreviewCardProps,
  IFormEntityPreviewCardState
> {
  static contextType = SheetContext
  declare context: React.ContextType<typeof SheetContext>
  private store: any
  private settings: Settings
  private formulaResolver = new AsyncFormulaResolver(() => this.forceUpdate())

  public static defaultProps: Partial<IFormEntityPreviewCardProps> = {
    entityEditSchemaPath: 'uiSchema.shared.entityEditSchema',
    showEntityType: false,
  }

  constructor(props) {
    super(props)
    this.store = apis.getStore()
    this.state = {
      entity: null,
      isLoading: false,
    }
    this.settings = apis.getSettings()
  }

  public componentWillUnmount() {
    this.formulaResolver.dispose()
  }

  public componentDidMount() {
    const { entity, value } = this.props
    if (entity?.entityId || value?.entityId) {
      this.fetchValueEntity(value?.entityId ?? entity?.entityId)
    } else if (entity) {
      this.setState({ entity, isLoading: false })
    }
  }

  public componentDidUpdate(prevProps) {
    const { entity, value } = this.props
    const entityId: string = value?.entityId ?? entity?.entityId ?? entity?.uniqueId
    const prevEntityId: string =
      prevProps?.value?.entityId ?? prevProps?.entity?.entityId ?? prevProps?.entity?.uniqueId

    if (entityId !== prevEntityId) {
      if (_.isNil(entityId)) {
        this.setState({
          entity: undefined,
          isLoading: false,
        })
      } else if (entity?.entityId || value?.entityId) {
        this.fetchValueEntity(value?.entityId ?? entity?.entityId)
      } else {
        this.setState({ entity, isLoading: false })
      }
    }
  }

  public render() {
    if (this.state.isLoading) {
      return (
        <div className="pa3 pt4 bt b--light-gray">
          <LoadingSpinner size="xs" />
        </div>
      )
    }

    return (
      <EntityPreviewCardContext.Consumer>
        {(cardCtx) => this.renderContent(cardCtx.showTypeStripe)}
      </EntityPreviewCardContext.Consumer>
    )
  }

  private renderContent(contextShowTypeStripe: boolean) {
    const { className, style } = this.props

    const resolution = this.resolveClick()
    const isInteractive = resolution !== 'none'
    const isMuted = !isInteractive

    const showTypeStripe = this.props.showTypeStripe ?? contextShowTypeStripe
    const display = showTypeStripe ? getWebEntityDisplay(this.state.entity) : undefined
    const StripeIcon = display?.Icon

    // FormEntityPreviewCard is never rendered standalone — it's always
    // mounted inside an entity-search field wrapper, a result-list
    // row, or another container that owns the outer form-group
    // chrome. The `.c-formGroup` wrapper that used to sit here just
    // added an extra layout node with no visible effect, so we drop
    // it and render the card as the component's root. `render()`
    // above still returns `null` when there's no entity, so the
    // "hasNoValue" branch is gone with it.
    if (!this.state.entity) return null
    return (
      <div
        className={classNames(
          'c-form-entity-preview-card',
          { 'c-form-entity-preview-card--isInteractive': isInteractive },
          { 'c-form-entity-preview-card--isMuted': isMuted },
          className
        )}
        style={style}
        role={isInteractive ? 'button' : undefined}
        tabIndex={0}
        onTouchStart={isInteractive ? (e) => this.handleOnClick(e, resolution) : undefined}
        onClick={isInteractive ? (e) => this.handleOnClick(e, resolution) : undefined}
        onKeyDown={(e) => {
          if (!isInteractive) return
          if (e.key === 'Enter') this.handleOnClick(e, resolution)
        }}
      >
        {display && (
          <div
            className="c-form-entity-preview-card--typeStripe"
            style={{ backgroundColor: display.color }}
          >
            {StripeIcon && <StripeIcon color="white" size={18} />}
          </div>
        )}
        <div className="c-form-entity-preview-card--body">
          <div className="c-form-entity-preview-card--headerRow">
            <div className="c-form-entity-preview-card--headerLeft">
              {this.renderTitleRow()}
              {this.renderIdentifierRow(this.getCells([EntityPreviewCardCellType.IDENTIFIER]))}
            </div>
            <div className="c-form-entity-preview-card--headerRight">
              {this.renderStatusRow(this.getCells([EntityPreviewCardCellType.STATUS]))}
            </div>
          </div>
          {this.renderInfoRow(
            this.getCells([
              EntityPreviewCardCellType.INFO,
              EntityPreviewCardCellType.INFO_FROM_ARRAY,
              EntityPreviewCardCellType.NOTES,
            ])
          )}
        </div>
      </div>
    )
  }

  private renderTitleRow() {
    const { entity } = this.state

    const title = this.props.title ?? entity?.displayName
    if (!title) {
      return null
    }
    return (
      <>
        {this.props.showEntityType && entity?.entityType && (
          <div className="c-form-entity-preview-card--label mb1">{entity.entityType}</div>
        )}
        <div className="c-form-entity-preview-card--title">{String(title)}</div>
      </>
    )
  }

  private renderIdentifierRow(filteredCells: IEntityPreviewCardCellProps[]) {
    if (filteredCells.length === 0) {
      return null
    }
    return (
      <div className="c-form-entity-preview-card--identifiers">
        {filteredCells.map((cell, i) => (
          <div key={i}>{cell.value}</div>
        ))}
      </div>
    )
  }

  private renderInfoRow(cells: IEntityPreviewCardCellProps[]) {
    const filteredCells = _.sortBy(cells, ['type'])

    if (filteredCells.length === 0) {
      return null
    }
    return (
      <div className="c-form-entity-preview-card--info">
        {filteredCells.map((cell, index) => (
          <div
            key={index}
            className={classNames('c-form-entity-preview-card--cell', {
              'c-form-entity-preview-card--cell-notes':
                cell.type === EntityPreviewCardCellType.NOTES,
            })}
          >
            {cell.label && <div className="c-form-entity-preview-card--label">{cell.label}</div>}
            <div className={classNames('c-form-entity-preview-card--cell-value')}>{cell.value}</div>
          </div>
        ))}
      </div>
    )
  }

  private renderStatusRow(filteredCells: IEntityPreviewCardCellProps[]) {
    if (filteredCells.length === 0) {
      return null
    }
    return (
      <div className="c-form-entity-preview-card--status">
        {filteredCells.map((cell, index) => (
          <StatusBadge key={index} text={cell.value} color={cell.color} />
        ))}
      </div>
    )
  }

  private getCells(types: EntityPreviewCardCellType[]): IEntityPreviewCardCellProps[] {
    const { data } = this.props
    const { entity } = this.state

    const customIdentifierCells: IEntityPreviewCardCellProps[] = _.compact(
      _.flatMap(data || [], (cell) => {
        if (cell.type === EntityPreviewCardCellType.INFO_FROM_ARRAY) {
          return this.buildCustomIdentifierCells(entity, cell)
        }
      })
    )

    return [...(data || []), ...(customIdentifierCells || [])]
      .map((cell) => this.getCellWithValue(cell, entity))
      .filter((cell) => {
        const exists = !_.isEmpty(cell) && !_.isNil(cell)
        const hasValue = !_.isNil(cell.value) && !_.isEmpty(cell.value)
        return (
          exists &&
          hasValue &&
          _.includes(types, cell.type) &&
          cell.type !== EntityPreviewCardCellType.INFO_FROM_ARRAY
        )
      })
  }

  private getCellWithValue(cell: IEntityPreviewCardCellProps, entity: Entity) {
    if (cell.path) {
      return {
        ...cell,
        value: objectToString(_.get(entity, cell.path, '')),
      }
    } else if (cell.formula) {
      return {
        value: this.evaluateCellFormula(cell.formula, entity),
        ...cell,
      }
    }
    return cell
  }

  private evaluateCellFormula(formula: string, entity: Entity) {
    return this.formulaResolver.resolve(`${entity?.uniqueId}::${formula}`, () =>
      evaluateExpressionWithScopes(this.props.frames, formula, {
        settings: this.settings,
        ...CustomFormulas,
        ...entity,
      })
    )
  }

  private buildCustomIdentifierCells(entity, cell) {
    const results = []
    let customIdentifierValues
    if (cell.path) {
      customIdentifierValues = _.get(entity, cell.path)
    } else if (cell.formula) {
      customIdentifierValues = this.evaluateCellFormula(cell.formula, entity)
    }
    const groupedIdentifiers = this.groupCustomIdentifiers(
      customIdentifierValues,
      cell.labelPath,
      cell.valuePath
    )
    for (const val of groupedIdentifiers || []) {
      results.push({
        label: val.label,
        value: objectToString(val.value),
        type: EntityPreviewCardCellType.INFO,
      })
    }
    return results
  }

  private groupCustomIdentifiers(
    identifierArray: any[],
    labelPath: string = 'label',
    valuePath: string = 'value'
  ): any[] {
    if (!Array.isArray(identifierArray)) {
      return []
    }
    const grouped = _.groupBy(identifierArray, (item) => item[labelPath])
    return _.map(grouped, (items, key) => {
      const values = _.map(items, (item) => item[valuePath])
      return {
        label: key,
        value: values.join(', '),
      }
    })
  }

  /**
   * Resolves the interaction matrix into a single outcome. See truth
   * table at the top of the file.
   */
  private resolveClick(): ClickResolution {
    const { isDisabled, isReadOnly, entityEditSchemaPath, entityReadOnlySchemaPath } = this.props
    const { entity } = this.state

    if (isDisabled || !entity) return 'none'

    const readonlyResolves =
      !!entityReadOnlySchemaPath && !!entity.resolveSubschemaByPath?.(entityReadOnlySchemaPath)

    if (isReadOnly) {
      return readonlyResolves ? 'readonly' : 'none'
    }

    const viewerOwnsEntity = this.viewerOwnsEntity(entity)
    if (viewerOwnsEntity) {
      // Owning firm — always attempts edit (falls back to new tab in
      // handleOnClick if `entityEditSchemaPath` fails to resolve).
      return 'edit'
    }

    // Non-owning viewer — degrade to read-only if a schema is defined,
    // otherwise muted no-op.
    return readonlyResolves ? 'readonly' : 'none'
  }

  private viewerOwnsEntity(entity: Entity): boolean {
    const viewerFirmId = this.settings?.firm?.uniqueId
    const ownerFirmId = _.get(entity, 'owner.firm.entityId')
    if (!viewerFirmId || !ownerFirmId) {
      // Missing ownership metadata — default to permissive (owning) so
      // pre-existing cards that lack owner metadata don't suddenly go
      // muted. Real cross-firm entities will always have owner.firm set.
      return true
    }
    return viewerFirmId === ownerFirmId
  }

  private handleOnClick = (event, resolution: ClickResolution) => {
    const { entityEditSchemaPath, entityReadOnlySchemaPath } = this.props
    const { entity } = this.state

    event.preventDefault()
    event.stopPropagation()

    if (resolution === 'readonly') {
      const subschema = entity.resolveSubschemaByPath(entityReadOnlySchemaPath)
      if (subschema) {
        this.openEntityFormModal(entityReadOnlySchemaPath, EntityFormSheetMode.View)
      }
      return
    }

    if (resolution === 'edit') {
      const subschema = entityEditSchemaPath && entity.resolveSubschemaByPath(entityEditSchemaPath)
      if (subschema) {
        this.openEntityFormModal(entityEditSchemaPath, EntityFormSheetMode.Edit)
      } else {
        window.open(`/entity/${entity.uniqueId}`, '_blank')
      }
    }
  }

  /**
   * Opens the entity form (edit / view) as a centered modal rather than
   * a slide-up sheet. Kept local to the card so the click behavior stays
   * self-contained — no ambient `SheetContext` required.
   */
  private openEntityFormModal = (uiSchemaPath: string, mode: EntityFormSheetMode) => {
    const { entity } = this.state
    OverlayManager.openOverlayElement(
      <Modal
        backdropClosesModal={true}
        modalDialogClassName='c-entityFormModal-dialog'
        modalPaperClassName='c-entityFormModal-paper'
      >
        <EntityFormModal
          entity={entity}
          entitySchema={entity?.schema}
          uiSchemaPath={uiSchemaPath}
          mode={mode}
          componentsMap={this.props.componentsMap}
          className={this.props.entityFormModalClassName}
        />
      </Modal>
    )
  }

  private fetchValueEntity(uniqueId: string) {
    this.setState({ isLoading: true })
    this.store.findRecordThrottled(uniqueId).then((entity) => {
      this.setState({
        entity,
        isLoading: false,
      })
    })
  }
}

/**
 * @deprecated Backward-compat wrapper for schemas that still declare
 * `label` / `labelProps` on the card itself. New callers should wrap the
 * card in `LabelFieldFactory` (or use `ui:form:entitySearchField` which
 * already handles labels). This helper will be removed once no schema
 * relies on card-level labels.
 */
export const FormEntityPreviewCardWithLabel: React.FC<
  IFormEntityPreviewCardProps & { label?: string; labelProps?: ILabelProps }
> = ({ label, labelProps, ...cardProps }) => {
  if (!label) {
    return <FormEntityPreviewCard {...cardProps} />
  }
  return (
    <div className="c-formGroup w-100">
      <Label text={label} {...labelProps} />
      <FormEntityPreviewCard {...cardProps} />
    </div>
  )
}
